<template>
    <div>
        <component :is="currentScreen"/>
    </div>
</template>

<script>
  /*
  *     "steps" is an array of translations. Every element of array is an array of steps in certain language
  */
  import {
    EVENT_SET_SCENARIO,
    EVENT_GA_SEND,
    EVENT_PREV_STEP,
    EVENT_NEXT_STEP,
    EVENT_DEMO_LINK
  } from '@/constants';

  import { mapState } from 'vuex';
  import { mapActions } from 'vuex';

  import mainScreenX from './screens/main-screen-x';
  import dashboardScreenSc10St2_5 from './screens/dashboard-screen-sc10-st2_5';
  import userProfileScreenSc10St7_9_12_14 from './screens/user-profile-screen-sc10-st7_9_12_14';
  import searchReportScreenSc10St10_11 from './screens/search-report-screen-sc10-st10_11';

  export default {
    name: "behavior-anomaly-discovery-screens",
    components: {
      mainScreenX,
      dashboardScreenSc10St2_5,
      userProfileScreenSc10St7_9_12_14,
      searchReportScreenSc10St10_11,
    },
    data() {
      return {
        product: 'CRsuite',
        creationLeadRequired: true,
        steps: [
          [
            {
              tooltip: {
                text: '<p>Alerts are great for getting notified when a single event occurs or a specific threshold is exceeded. But what if an attack involves a chain of actions stretching across time? Let’s learn how Netwrix Auditor can help you detect these more sophisticated threats.</p><p>Click <b>Behavior anomalies</b> to get started.</p>',
                showSteps: true,
                actions: [],
              },
              viewArea: {
                x: 877,
                y: 505,
                width: 240,
                height: 93,
              },
              screen: 'main-screen-x',
            },
            {
              tooltip: {
                alignArrow: 22,
                hideArrow: true,
                align: 38,
                offset: -418,
                text: '<p>Meet the <b>Behavior Anomalies</b> dashboard. It provides a single view of abnormal user activity across your entire IT environment.</p><p>Click <b>Next</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 100,
                width: 1429,
                height: 686,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>At the left, there’s a list of users whose activity is considered abnormal because they’ve made unusual or critical changes across the audited IT systems. Users with the riskiest behavior are always at the top, so you’ll usually examine the list from top to bottom, unless you’re interested in the activity of a specific user.</p><p>Click <b>Next</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 649,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>How does Netwrix Auditor determine which users represent the most risk? By looking at the alerts they triggered. However, not every single alert is used. To be included here, alerts have to be assigned a risk score — a customizable value that reflects how critical the action is for your organization.</p><p>At the right, you can find each user’s risk score. This number reflects the total risk score of all alerts triggered by that user. Nine times out of ten, a high risk score means something fishy is going on.</p><p>Click <b>Next</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 780,
                y: 327,
                width: 322,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>At the top of the dashboard is the <b>Risk Score Timeline</b> chart. It is useful if you need to quickly understand exactly when there have been spikes in abnormal user activity.</p><p>Click <b>Next</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 102,
                width: 1429,
                height: 227,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 505,
                position: 'bottom',
                text: '<p>Now it’s time to get down to the real work with the Behavior Anomalies dashboard. The user account <b>ENTEPRISE\\T.Simpson</b> is at the top of our list of risky users. T.Simpson is leaving the company soon. Why is his risk score so high? Let’s drill down into his profile to see what he’s done to get such a high risk score.</p><p><b>Double-click</b> T.Simpson’s account to start our investigation.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 354,
                width: 1429,
                height: 36,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                offset: -200,
                text: '<p>Here is the list of alerts triggered by T.Simpson in the last 30 days. Scroll down to review them and click <b>Next</b> when you’re ready to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: '50%',
                        },
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 1136,
                height: 867,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                width: 482,
                text: '<p>Okay, lots of alerts were triggered by the creation of potentially harmful files on file storages and SharePoint. Also, there were alerts on failed access events and mass data removal from a file share.</p><p>That’s getting more and more suspicious. Let’s review all of T.Simpson’s activity; he might have also done something that is not yet included in the Behavior Anomalies dashboard.</p><p>Click <b>Next</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 151,
                width: 929,
                height: 166,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Now click the <b>Show user activity</b> button to open the Interactive Search showing all of T.Simpson’s activity.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 220,
                width: 162,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Here it is. Take some time to scroll down and pick out events that seem most suspicious to you. We’ll compare our findings in the next step.</p><p>Hit <b>Next</b> when you’re finished.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Okay, first T.Simpson tried and failed to access several folders. Then he deleted multiple files from a file share and placed harmful executables there. He also placed a handful of them on SharePoint. By the names of these files, we can tell they might be malicious code that exports data to somewhere else.</p><p>To top it all off, he inserted a USB drive in his boss’s machine. Was he copying company data before he leaves? Let’s give all the evidence we have to the legal team and let them handle this.</p><p>Click <b>Next</b> to go back to our dashboard.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Next',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                position: 'top',
                text: '<p>Okay, now that we’ve examined all the alerts triggered by T.Simpson and all his activity, and provided the legal team with what we found, we can mark this all as reviewed.</p><p>Click <b>Mark all as reviewed</b> to continue.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 634,
                width: 181,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                hideViewArea: true,
                text: '<p>Let’s add a comment so we don’t forget what our review turned up, and to let coworkers know that it has been taken care of.</p><p>Type the following in the comment window: <b>Possible data theft and sabotage by a departing employee. The incident is being reviewed by the legal team.</b></p><p>Then click the <b>OK</b> button to close the window.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Back',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 580,
                y: 118,
                width: 521,
                height: 613,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                alignArrow: 11,
                hideViewArea: true,
                hideArrow: true,
                text: '<p>That’s it for this scenario. But I’m happy to show you some other useful things you can do with Netwrix Auditor; just click <b>Continue Tour.</b></p><p>If you’re ready to set up a one-on-one demo with one of our engineers, though, I won’t be offended in the least! Just click <b>Request 1-1 Demo.</b></p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Continue Tour',
                    id: 'sc1_st14',
                    events: [
                      {
                        type: EVENT_SET_SCENARIO,
                        value: 0,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: 'Completed',
                        },
                      },
                    ],
                  },
                  {
                    text: 'Request 1-1 Demo',
                    events: [
                      {
                        type: EVENT_DEMO_LINK,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 100,
                width: 870,
                height: 800,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
          ],
          [
            {
              tooltip: {
                text: '<p>Les alertes sont un bon moyen d’être averti lorsqu’un événement particulier se produit ou lorsqu’un seuil spécifique est dépassé. Mais... et si une attaque impliquait une série d’actions étalées dans le temps ? Découvrez comment Netwrix Auditor peut vous aider à détecter ces menaces sophistiquées.</p><p>Pour commencer, cliquez sur <b>Behavior anomalies</b> (Anomalies comportementales).</p>',
                showSteps: true,
                actions: [],
              },
              viewArea: {
                x: 877,
                y: 505,
                width: 240,
                height: 93,
              },
              screen: 'main-screen-x',
            },
            {
              tooltip: {
                alignArrow: 22,
                hideArrow: true,
                align: 38,
                offset: -418,
                text: '<p>Voici le tableau de bord <b>Behavior Anomalies</b> (Anomalies comportementales). Il offre une vue centralisée des activités anormales des utilisateurs dans tout votre environnement informatique.</p><p>Cliquez sur <b>Suivant</b> pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 100,
                width: 1429,
                height: 686,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>À gauche, vous trouvez une liste d’utilisateurs dont l’activité est considérée comme anormale parce qu’ils ont apporté des modifications inhabituelles ou critiques dans les systèmes informatiques audités. Les utilisateurs dont le comportement présente le plus de risques sont toujours placés en haut de la liste. Vous consulterez donc généralement cette liste de haut en bas, sauf si vous vous intéressez à l’activité d’un utilisateur en particulier.</p><p>Cliquez sur <b>Suivant</b> pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 649,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>Comment Netwrix Auditor détermine-t-il quels utilisateurs représentent le plus de risques ? En analysant les alertes qu’ils ont déclenchées. Mais toutes les alertes ne sont pas prises en compte. Pour être incluses, les alertes reçoivent un score de risque : une valeur personnalisable qui reflète le caractère critique de l’action correspondante pour votre organisation.</p><p>À droite, le score de risque de chaque utilisateur s’affiche. Ce nombre reflète le score de risque total de toutes les alertes déclenchées par cet utilisateur. Neuf fois sur dix, un score de risque élevé indique qu’il se passe quelque chose de louche.</p><p>Cliquez sur <b>Suivant</b> pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 780,
                y: 327,
                width: 322,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Dans la partie haute du tableau de bord se trouve le <b>tableau chronologique des scores de risque</b>. Il est utile si vous devez savoir rapidement et exactement quand il y a eu des pics d’activité anormale de la part des utilisateurs.</p><p>Cliquez sur <b>Suivant</b> pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 102,
                width: 1429,
                height: 227,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 505,
                position: 'bottom',
                text: '<p>Il est temps à présent de passer aux choses sérieuses avec le tableau de bord des anomalies comportementales. Le compte d’utilisateur <b>ENTEPRISE\\T.Simpson</b> figure en tête de notre liste d’utilisateurs à risque. T.Simpson va bientôt quitter l’entreprise. Pourquoi son score de risque est-il si élevé ? Examinons son profil en détail pour voir ce qu’il a fait pour obtenir un score aussi élevé.</p><p><b>Double-cliquez</b> sur le compte de T.Simpson pour commencer l’enquête.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 354,
                width: 1429,
                height: 36,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                offset: -200,
                text: '<p>Voici la liste des alertes déclenchées par T.Simpson au cours des 30 derniers jours. Faites défiler vers le bas pour les passer en revue, puis cliquez sur <b>Suivant</b> lorsque vous êtes prêt à continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: '50%',
                        },
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 1136,
                height: 867,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                width: 482,
                text: '<p>Bien, de nombreuses alertes ont été déclenchées par la création de fichiers potentiellement dangereux dans les stockages de fichiers et dans SharePoint. Il y a également eu des alertes concernant des échecs d’accès et des suppressions massives de données d’un partage de fichiers.</p><p>Cela devient de plus en plus suspect. Examinons toutes les activités de T.Simpson ; il a peut-être aussi fait quelque chose qui ne figure pas encore dans le tableau de bord des anomalies comportementales.</p><p>Cliquez sur <b>Suivant</b> pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 151,
                width: 929,
                height: 166,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Cliquez à présent sur le bouton <b>Show user activity</b> (Afficher les activités de l’utilisateur) pour lancer la recherche interactive affichant toutes les activités de T.Simpson.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 220,
                width: 162,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Voilà. Faites défiler la liste et sélectionnez les événements qui vous semblent les plus suspects. Nous allons comparer nos conclusions lors de la prochaine étape.</p><p>Cliquez sur <b>Suivant</b> dès que vous êtes prêt.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Alors, T.Simpson a d’abord essayé d’accéder à plusieurs dossiers, mais sans succès. Il a ensuite supprimé plusieurs fichiers d’un partage de fichiers et y a placé des exécutables malveillants. Il en a aussi mis quelques-uns dans SharePoint. D’après les noms de ces fichiers, on peut supposer qu’il s’agit de codes malveillants qui exportent des données vers un autre endroit.</p><p>Pour couronner le tout, il a inséré une clé USB dans la machine de son patron. A-t-il copié les données de l’entreprise avant de partir ? Communiquons toutes les preuves dont nous disposons au service juridique et laissons-les s’en occuper.</p><p>Cliquez sur <b>Suivant</b> pour revenir au tableau de bord.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Suivant',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                position: 'top',
                text: '<p>Bien, maintenant que nous avons examiné toutes les alertes déclenchées par T.Simpson et toutes ses activités, et que nous avons fourni à l’équipe juridique nos découvertes, nous pouvons indiquer que tout a été examiné.</p><p>Cliquez sur <b>Mark all as reviewed</b> (Tout marquer comme examiné) pour continuer.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 634,
                width: 181,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                hideViewArea: true,
                text: '<p>Ajoutons un commentaire pour ne pas oublier ce que notre enquête a révélé et pour faire savoir à nos collègues que le problème a été pris en charge.</p><p>Saisissez le texte suivant dans la fenêtre de commentaire : <b>Possible data theft and sabotage by a departing employee. The incident is being reviewed by the legal team.</b></p><p>Ensuite, cliquez sur le bouton <b>OK</b> pour fermer la fenêtre.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Précédent',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 580,
                y: 118,
                width: 521,
                height: 613,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 545,
                alignArrow: 11,
                hideViewArea: true,
                hideArrow: true,
                text: '<p>C’est tout pour ce scénario ! Je serais ravi de vous montrer d’autres rapports ou d’autres choses utiles réalisables avec Netwrix Auditor ; il vous suffit de cliquer sur <b>Poursuivre la visite.</b></p><p>Si vous êtes prêt à planifier une démonstration personnalisée avec l’un de nos ingénieurs, cliquez sur <b>Demander une démo individuelle.</b></p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Poursuivre la visite',
                    id: 'sc1_st14',
                    events: [
                      {
                        type: EVENT_SET_SCENARIO,
                        value: 0,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: 'Completed',
                        },
                      },
                    ],
                  },
                  {
                    text: 'Demander une démo individuelle',
                    events: [
                      {
                        type: EVENT_DEMO_LINK,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 100,
                width: 870,
                height: 800,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
          ],
          [
            {
              tooltip: {
                text: '<p>Warnmeldungen sind eine hervorragende Methode, um sich über bestimmte Ereignisse oder das Überschreiten von Schwellenwerten benachrichtigen zu lassen. Doch wie können Sie Angriffe aufdecken, die aus mehreren Aktionen über einen längeren Zeitraum hinweg bestehen? Wir zeigen Ihnen, wie Sie mit Netwrix Auditor komplexe Bedrohungen dieser Art erkennen können.</p><p>Klicken Sie auf <b>Behavior anomalies</b>, um zu beginnen.</p>',
                showSteps: true,
                actions: [],
              },
              viewArea: {
                x: 877,
                y: 505,
                width: 240,
                height: 93,
              },
              screen: 'main-screen-x',
            },
            {
              tooltip: {
                alignArrow: 22,
                hideArrow: true,
                align: 38,
                offset: -418,
                text: '<p>Hier sehen Sie das Dashboard <b>Behavior Anomalies</b>. Es enthält eine Übersicht über alle ungewöhnlichen Benutzeraktivitäten in Ihrer gesamten IT-Umgebung.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 100,
                width: 1429,
                height: 686,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>Auf der linken Seite wird eine Liste der Benutzer angezeigt, deren Aktivitäten als ungewöhnlich eingestuft werden, da sie untypische oder kritische Änderungen an den überwachten IT-Systemen vorgenommen haben. Benutzer mit besonders riskantem Verhalten werden ganz oben in der Liste aufgeführt. Gehen Sie die Liste deshalb der Reihe nach durch, sofern Sie nicht gezielt die Aktivitäten eines bestimmten Benutzers überprüfen möchten.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 649,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 430,
                text: '<p>Wie kann Netwrix Auditor feststellen, welche Benutzer das größte Risiko darstellen? Mithilfe der Warnmeldungen, die durch diese Aktivitäten ausgelöst werden. Dabei werden jedoch nicht alle Warnmeldungen berücksichtigt. Die Liste enthält ausschließlich Warnmeldungen, für die eine Risikoeinstufung festgelegt wurde. Diese besteht aus einem anpassbaren Wert, der Aufschluss darüber gibt, wie kritisch eine Aktion für Ihr Unternehmen ist.</p><p>Auf der rechten Seite sehen Sie die Risikoeinstufung für die einzelnen Benutzer. Dieser Wert entspricht der Gesamtrisikoeinstufung aller Warnmeldungen, die durch die Aktivitäten dieses Benutzers ausgelöst wurden. In 90 Prozent der Fälle ist eine hohe Risikoeinstufung ein Hinweis auf unerlaubte Aktivitäten.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 780,
                y: 327,
                width: 322,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Im oberen Bereich des Dashboards sehen Sie die grafische Darstellung <b>Risk Score Timeline</b>. Damit können Sie schnell erkennen, wann es besonders viele ungewöhnliche Benutzeraktivitäten gab.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 102,
                width: 1429,
                height: 227,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 505,
                position: 'bottom',
                text: '<p>Sie werden das Dashboard <b>Behavior Anomalies</b> nun für eine genauere Untersuchung nutzen. Ganz oben in der Liste der riskanten Benutzer wird das Benutzerkonto <b>ENTEPRISE\\T.Simpson</b> angezeigt. T. Simpson wird das Unternehmen demnächst verlassen. Warum weist sein Konto eine so hohe Risikoeinstufung auf? Sehen Sie sich das Profil genauer an, um zu untersuchen, wie diese hohe Risikoeinstufung zustande kommt.</p><p><b>Doppelklicken</b> Sie auf das Konto von T. Simpson, um mit der Untersuchung zu beginnen.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 354,
                width: 1429,
                height: 36,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                offset: -200,
                text: '<p>Sie sehen hier eine Liste der Warnmeldungen, die durch die Aktivitäten von T. Simpson in den vergangenen 30 Tagen ausgelöst wurden. Blättern Sie durch die Liste und klicken Sie anschließend auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: '50%',
                        },
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 1136,
                height: 867,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                width: 482,
                text: '<p>Gut. Viele der Warnmeldungen sind auf die Erstellung potenziell schädlicher Dateien auf Dateispeichersystemen und in SharePoint zurückzuführen. Es gibt außerdem Warnmeldungen zu fehlgeschlagenen Zugriffsversuchen und einer Massenlöschung von Daten auf einer Dateifreigabe.</p><p>Das sieht schon wesentlich verdächtiger aus. Überprüfen Sie daher sämtliche Aktivitäten von T. Simpson – möglicherweise sind im Dashboard Behavior Anomalies auch noch gar nicht alle Aktivitäten dieses Benutzers erfasst.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 151,
                width: 929,
                height: 166,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Klicken Sie nun auf die Schaltfläche <b>Show user activity</b>, um die interaktive Suche zur Anzeige aller Aktivitäten von T. Simpson zu öffnen.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 220,
                width: 162,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Und hier ist das Ergebnis. Blättern Sie in Ruhe durch die Liste und klicken Sie auf die Ereignisse, die Ihnen besonders verdächtig erscheinen. Im nächsten Schritt werden wir die Ergebnisse genauer untersuchen.</p><p>Klicken Sie auf <b>Weiter</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>T. Simpson hat zunächst erfolglos versucht, auf verschiedene Ordner zuzugreifen. Er hat dann mehrere Dateien auf einer Dateifreigabe gelöscht und schädliche ausführbare Dateien auf die Freigabe kopiert. Einige dieser Dateien hat er außerdem in SharePoint platziert. Die Namen der Dateien lassen erkennen, dass sie sehr wahrscheinlich Schadsoftware enthalten, mit der Daten auf ein anderes System exportiert werden.</p><p>Darüber hinaus hat er ein USB-Laufwerk an den Rechner seines Chefs angeschlossen. Hat er etwa vor seinem Ausscheiden Unternehmensdaten kopiert? Dieses Beweismaterial sollte an die Rechtsabteilung übermittelt werden, damit diese die nötigen Schritte einleitet.</p><p>Klicken Sie auf <b>Weiter</b>, um zum Dashboard zurückzukehren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Weiter',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                position: 'top',
                text: '<p>Sie haben nun alle durch T. Simpson ausgelösten Warnmeldungen sowie seine gesamten Aktivitäten untersucht und Ihre Ergebnisse an die Rechtsabteilung weitergeleitet. Sie können diese Warnmeldungen daher als überprüft kennzeichnen.</p><p>Klicken Sie auf <b>Mark all as reviewed</b>, um fortzufahren.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 634,
                width: 181,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                hideViewArea: true,
                text: '<p>Geben Sie einen Kommentar ein, damit Sie nicht vergessen, welche Ergebnisse die Untersuchung erbracht hat, und damit auch Ihre Kollegen wissen, dass Sie den Warnmeldungen nachgegangen sind.</p><p>Geben Sie in das Kommentarfenster Folgendes ein: <b>Possible data theft and sabotage by a departing employee. The incident is being reviewed by the legal team.</b></p><p>Klicken Sie anschließend auf <b>OK</b>, um das Fenster zu schließen.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Zurück',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 580,
                y: 118,
                width: 521,
                height: 613,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 488,
                alignArrow: 11,
                hideViewArea: true,
                hideArrow: true,
                text: '<p>Wir sind am Ende dieses Szenarios angekommen. Gerne zeige ich Ihnen noch andere hilfreiche Funktionen von Netwrix Auditor. Klicken Sie hierzu einfach auf <b>Tour fortsetzen.</b></p><p>Wenn Sie eine persönliche Demo mit einem unserer Engineers wünschen, klicken Sie bitte auf <b>Persönliche Demo anfordern.</b></p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Tour fortsetzen',
                    id: 'sc1_st14',
                    events: [
                      {
                        type: EVENT_SET_SCENARIO,
                        value: 0,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: 'Completed',
                        },
                      },
                    ],
                  },
                  {
                    text: 'Persönliche Demo anfordern',
                    events: [
                      {
                        type: EVENT_DEMO_LINK,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 100,
                width: 870,
                height: 800,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
          ],
          [
            {
              tooltip: {
                text: '<p>Gli avvisi sono ottimi per ricevere notifiche quando si verifica un singolo evento o se viene superata una determinata soglia. Ma cosa succede se un attacco provoca una serie di azioni che si protrae nel tempo? Scopriamo in che modo Netwrix Auditor può aiutarti a rilevare queste minacce più sofisticate.</p><p>Fai clic su <b>Behavior anomalies</b> per iniziare.</p>',
                showSteps: true,
                actions: [],
              },
              viewArea: {
                x: 877,
                y: 505,
                width: 240,
                height: 93,
              },
              screen: 'main-screen-x',
            },
            {
              tooltip: {
                alignArrow: 22,
                hideArrow: true,
                align: 38,
                offset: -418,
                text: '<p>Scopri la dashboard <b>Behavior Anomalies</b>. Fornisce una prospettiva unica delle attività anomale compiute dagli utenti nell\'intero ambiente IT.</p><p>Fai clic su <b>Avanti</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 100,
                width: 1429,
                height: 686,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>A sinistra c\'è un elenco di utenti la cui attività è considerata anomala, poiché hanno apportato modifiche insolite o critiche ai sistemi IT ispezionati. Gli utenti che hanno messo in atto le attività più pericolose sono sempre posti in alto, quindi di norma l\'elenco viene analizzato dall\'alto verso il basso, a meno che tu non sia interessato all\'attività di un utente specifico.</p><p>Fai clic su <b>Avanti</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 649,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>In che modo Netwrix Auditor individua quali sono gli utenti che rappresentano il rischio maggiore? Osservando gli avvisi che hanno attivato. Tuttavia, non si utilizzano i singoli avvisi. Per essere inseriti qui, ad ogni avviso deve essere assegnato un punteggio di rischio, un valore personalizzabile che rappresenta quanto quell’azione sia critica per la tua azienda.</p><p>A destra c’è il punteggio di rischio di ciascun utente. Questo numero rappresenta il punteggio di rischio totale di tutti gli avvisi attivati da quell\'utente. Nove volte su dieci, un punteggio ad alto rischio significa che sta succedendo qualcosa di sospetto.</p><p>Fai clic su <b>Avanti</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 780,
                y: 327,
                width: 322,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Nella parte superiore della dashboard si trova il grafico <b>Risk Score Timeline</b>. Ti sarà utile se hai bisogno di capire velocemente l’esatto momento in cui si sono verificati i picchi di attività utente anomala.</p><p>Fai clic su <b>Avanti</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 102,
                width: 1429,
                height: 227,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 505,
                position: 'bottom',
                text: '<p>Ora è il momento di dedicarti al vero lavoro con la dashboard Behavior Anomalies. L\'account utente <b>ENTEPRISE\\ T.Simpson</b> è in cima alla nostra lista di utenti a rischio. T.Simpson lascerà presto l\'azienda. Perché il suo punteggio di rischio è così alto? Analizziamo il suo profilo per vedere cosa ha fatto per ottenere un punteggio di rischio così elevato.</p><p>Fai <b>Double-click</b> sull\'account di T.Simpson per iniziare la ricerca.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 354,
                width: 1429,
                height: 36,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                offset: -200,
                text: '<p>Ecco l\'elenco degli avvisi attivati da T.Simpson negli ultimi 30 giorni. Scorri verso il basso per esaminarli e fai clic su <b>Avanti</b> quando sei pronto per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: '50%',
                        },
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 1136,
                height: 867,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                width: 482,
                text: '<p>Bene, sono stati attivati svariati avvisi a seguito della creazione di file potenzialmente dannosi su archivi di file e SharePoint. Inoltre, sono stati attivati alcuni avvisi per eventi di accesso non riusciti e rimozione di dati di massa da una condivisione di file.</p><p>La cosa diventa sempre più sospetta. Controlliamo tutte le attività di T.Simpson; potrebbe anche aver fatto qualcosa che non è ancora stato incluso nella dashboard Behavior Anomalies.</p><p>Fai clic su <b>Avanti</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 151,
                width: 929,
                height: 166,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Ora fai clic sul pulsante <b>Show user activity</b> per aprire la Ricerca interattiva che mostra tutte le attività di T.Simpson.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 220,
                width: 162,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Ecco qui. Prenditi un po’ di tempo per scorrere verso il basso e selezionare gli eventi che ti sembrano più sospetti. Confronteremo i nostri risultati nel prossimo passaggio.</p><p>Premi <b>Avanti</b> quando hai finito.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Bene, per prima cosa T.Simpson ha provato (ma non è riuscito) ad accedere a diverse cartelle. Quindi ha eliminato più file da una condivisione file e vi ha inserito file eseguibili dannosi. Ne ha inseriti alcuni anche su SharePoint. Dai nomi di questi file, possiamo dire che potrebbero essere codici dannosi che esportano dati da qualche altra parte.</p><p>Per finire, ha inserito un\'unità USB nel computer del suo capo. Stava forse facendo una copia dei dati dell\'azienda prima di andare via? Forniamo tutte le prove che abbiamo raccolto al team legale, che gestirà la situazione.</p><p>Fai clic su <b>Avanti</b> per tornare alla nostra dashboard.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Avanti',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                position: 'top',
                text: '<p>Bene, ora che abbiamo esaminato tutti gli avvisi attivati da T.Simpson e tutte le sue attività, nonché fornito al team legale le informazioni che abbiamo scoperto, possiamo contrassegnare tutto come già controllato.</p><p>Fai clic su <b>Mark all as reviewed</b> per continuare.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 634,
                width: 181,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                hideViewArea: true,
                text: '<p>Aggiungiamo un commento, in modo da non dimenticare ciò che è emerso dal nostro controllo e far sapere ai colleghi che è stato risolto.</p><p>Digitare quanto segue nella finestra dei commenti: <b>Possible data theft and sabotage by a departing employee. The incident is being reviewed by the legal team</b>.</p><p>Quindi fare clic sul pulsante <b>OK</b> per chiudere la finestra.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Indietro',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 580,
                y: 118,
                width: 521,
                height: 613,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                alignArrow: 11,
                hideViewArea: true,
                hideArrow: true,
                text: '<p>Questo è quanto per quel che riguarda questo scenario. Ma sono felice di mostrarti altre cose utili che puoi fare con Netwrix Auditor; basta fare clic su <b>Continua Tour.</b></p><p>Tuttavia, se sei pronto per una demo one-to-one con uno dei nostri ingegneri, non mi offenderò affatto! Basta fare clic su <b>Richiedi Demo 1-1.</b></p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Continua Tour',
                    id: 'sc1_st14',
                    events: [
                      {
                        type: EVENT_SET_SCENARIO,
                        value: 0,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: 'Completed',
                        },
                      },
                    ],
                  },
                  {
                    text: 'Richiedi Demo 1-1',
                    events: [
                      {
                        type: EVENT_DEMO_LINK,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 100,
                width: 870,
                height: 800,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
          ],
          [
            {
              tooltip: {
                text: '<p>Оповещения полезны, когда происходит один инцидент или превышается определенный порог. А что если атака состоит из нескольких действий, которые совершились за долгий промежуток времени? Давайте посмотрим, как Netwrix Auditor может помочь вам распознать такие более сложные угрозы.</p><p>Нажмите на <b>Behavior anomalies</b> (Аномалии поведения), чтобы начать.</p>',
                showSteps: true,
                actions: [],
              },
              viewArea: {
                x: 877,
                y: 505,
                width: 240,
                height: 93,
              },
              screen: 'main-screen-x',
            },
            {
              tooltip: {
                alignArrow: 22,
                hideArrow: true,
                align: 38,
                offset: -418,
                text: '<p>Перед вами дашборд Behavior Anomalies (Аномалии поведения). В нем собрана вся информация об аномальных активностях пользователей во всей вашей инфраструктуре.</p><p>Нажмите <b>Далее</b>, чтобы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 100,
                width: 1429,
                height: 686,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                text: '<p>Слева список пользователей, которые проявили аномальную активность, например, необычные или критические изменения в ИТ-системах, аудит которых вы проводите. Пользователи с наиболее рискованным поведением находятся наверху списка, поэтому рекомендую смотреть этот список сверху вниз, если только вы не ищете конкретного пользователя.</p><p>Нажмите <b>Далее</b>, чтобы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 649,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 415,
                text: '<p>Как Netwrix Auditor определяет, какие пользователи представляют наибольший риск? По оповещениям, которые они спровоцировали. Однако, не каждое оповещение учитывается. Чтобы попасть в этот список, у оповещения должно быть значение риска – вы настраиваете значения согласно тому, какие действия пользователей критичны для вашей организации.</p><p>Справа вы можете видеть значение риска каждого пользователя (Risk score). Это сумма значений риска всех оповещений, которые вызваны действиями этого пользователя. В 9 случаях из 10 высокое значение риска означает что-то действительно опасное.</p><p>Нажмите <b>Далее</b>, чтобы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 780,
                y: 327,
                width: 322,
                height: 459,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Наверху этого дашборда график Risk Score Timeline. Он пригодится, когда вам нужно будет быстро определить, когда именно были вспышки аномальных активностей пользователей.</p><p>Нажмите <b>Далее</b>, чтобы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 102,
                width: 1429,
                height: 227,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                width: 505,
                position: 'bottom',
                text: '<p>Давайте поработаем с дашбордом. Пользовательский аккаунт ENTEPRISE\\T.Simpson возглавляет список пользователей с опасным поведением. T.Simpson скоро уходит из компании. Почему у него такое высокое значение риска? Давайте изучим его профиль, чтобы разобраться, что он сделал.</p><p><b>Двойным кликом</b> выберите аккаунт T.Simpson, чтобы начать наше расследование.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 354,
                width: 1429,
                height: 36,
              },
              screen: 'dashboard-screen-sc10-st2_5',
            },
            {
              tooltip: {
                offset: -200,
                text: '<p>Здесь представлен список всех оповещений, вызванных действиями T.Simpson за последние 30 дней. Прокрутите вниз, чтобы посмотреть их и нажмите <b>Далее</b>, когда будете готовы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: '50%',
                        },
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 327,
                width: 1136,
                height: 867,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                width: 482,
                text: '<p>Итак, много оповещений произошло из-за создания потенциально опасных файлов на файловых хранилищах и SharePoint. Помимо этого, были оповещения о безуспешных попытках доступа и массовом удалении данных с файлового хранилища.</p><p>Становится всё более подозрительно. Давайте просмотрим всю активность T.Simpson: он мог сделать что-то, что еще не попало в дашборд Behavior Anomalies.</p><p>Нажмите <b>Далее</b>, чтобы продолжить.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 151,
                width: 929,
                height: 166,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'bottom',
                text: '<p>Теперь нажмите кнопку <b>Show user activity</b> (показать активность пользователя), чтобы открыть Интерактивный поиск и все активности T.Simpson.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 220,
                width: 162,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Вот он. Изучите эту информацию и выберите самые подозрительные для вас события. Мы сравним наш выбор в следующем шаге.</p><p>Нажмите <b>Далее</b>, когда закончите.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                align: -0.05,
                text: '<p>Так, во-первых, T.Simpson безуспешно пытался получить доступ к нескольким папкам. Потом он удалил несколько файлов из хранилища и добавил вредоносные файлы в хранилище и SharePoint. Судя по названиям файлов, похоже, что это вредоносный код, который куда-то экспортирует данные.</p><p>Помимо всего этого, он вставил флешку в компьютер своего начальника. Неужели он копировал корпоративные данные перед уходом? Давайте передадим всю найденную информацию в юридический отдел.</p><p>Нажмите <b>Далее</b>, и мы вернемся в наш дашборд.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                  {
                    text: 'Далее',
                    events: [
                      {
                        type: EVENT_NEXT_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 130,
                y: 204,
                width: 1045,
                height: 1934,
              },
              screen: 'search-report-screen-sc10-st10_11',
            },
            {
              tooltip: {
                position: 'top',
                text: '<p>Теперь поскольку мы изучили все оповещения о действиях T.Simpson и переслали информацию юристам, можем пометить, что всё это было нами просмотрено.</p><p>Чтобы продолжить, нажмите <b>Mark all as reviewed</b> (пометить всё, как просмотренное).</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                x: 1278,
                y: 634,
                width: 181,
                height: 38,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 413,
                hideViewArea: true,
                text: '<p>Давайте добавим комментарий, чтобы не забыть, что мы выяснили, и чтобы коллеги видели, что с ситуацией уже разбираются.</p><p>В поле комментария напечатайте: <b>Possible data theft and sabotage by a departing employee. The incident is being reviewed by the legal team.</b> (Возможная кража данных уходящим сотрудником. Информация передана юристам.)</p><p>Затем нажмите кнопку <b>OK</b>, чтобы закрыть окно.</p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Назад',
                    events: [
                      {
                        type: EVENT_PREV_STEP,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 580,
                y: 118,
                width: 521,
                height: 613,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
            {
              tooltip: {
                position: 'left',
                width: 544,
                alignArrow: 11,
                hideViewArea: true,
                hideArrow: true,
                text: '<p>Мы закончили этот сценарий демо в браузере. Буду рад показать вам другие возможности Netwrix Auditor – просто нажмите <b>Продолжить обзор.</b></p><p>Но если вы хотите договориться с нашими инженерами об индивидуальной демонстрации, я не обижусь! Для этого нажмите <b>Запросить персональное демо.</b></p>',
                showSteps: true,
                actions: [
                  {
                    text: 'Продолжить обзор',
                    id: 'sc1_st14',
                    events: [
                      {
                        type: EVENT_SET_SCENARIO,
                        value: 0,
                      },
                      {
                        type: EVENT_GA_SEND,
                        value: {
                          scenario: 'Behavior Anomaly Discovery',
                          stage: 'Completed',
                        },
                      },
                    ],
                  },
                  {
                    text: 'Запросить персональное демо',
                    events: [
                      {
                        type: EVENT_DEMO_LINK,
                      },
                    ],
                  },
                ],
              },
              viewArea: {
                scrollToView: true,
                x: 230,
                y: 100,
                width: 870,
                height: 800,
              },
              screen: 'user-profile-screen-sc10-st7_9_12_14',
            },
          ],
        ],
      }
    },
    computed: {
      ...mapState('guide', ['currentScreen']),
      language() {
        return this.$store.state.guide.lang;
      },
    },
    methods: {
      ...mapActions('guide', ['setSteps']),
    },
    created() {
      this.setSteps(this.steps[this.language]);
    },
  }
</script>

<style scoped>

</style>